import { getReferenceString } from "@medplum/core";
import { ServiceRequest } from "@medplum/fhirtypes";

import { createEntityAdapter, createSelector, createSlice } from "@reduxjs/toolkit";

import { joinReferenceString } from "@/utils/medplum";

import { RootState } from "..";

const labOrdersAdapter = createEntityAdapter({
  selectId: (labOrder: ServiceRequest) => labOrder.id!,
});

const reducers = {
  loadLabOrders: labOrdersAdapter.setAll,
  addLabOrder: (
    state: ReturnType<typeof labOrdersAdapter.getInitialState>,
    action: { payload: ServiceRequest },
  ) => {
    // Adds new lab orders to the beginning of the list
    const labOrder = action.payload;
    if (labOrder.id && !state.entities[labOrder.id]) {
      state.ids.unshift(labOrder.id);
      state.entities[labOrder.id] = labOrder;
    }
  },
  updateLabOrder: labOrdersAdapter.updateOne,
  removeLabOrder: labOrdersAdapter.removeOne,
};

export const labOrdersSlice = createSlice({
  name: "labOrders",
  initialState: labOrdersAdapter.getInitialState(),
  reducers,
});

export const { loadLabOrders, addLabOrder, updateLabOrder, removeLabOrder } =
  labOrdersSlice.actions;

const selectLabOrdersIds = (state: RootState) => state.labOrders.ids;
const selectLabOrdersEntities = (state: RootState) => state.labOrders.entities;

export const selectLabOrdersForPractice = (practiceId: string | undefined) =>
  createSelector([selectLabOrdersIds, selectLabOrdersEntities], (ids, entities): ServiceRequest[] =>
    ids
      .map((id) => entities[id])
      .filter(
        (labOrder): labOrder is ServiceRequest =>
          !!labOrder &&
          !!labOrder.meta?.compartment &&
          !!practiceId &&
          labOrder.meta.compartment.some(
            (compartment) =>
              compartment.reference === joinReferenceString("Organization", practiceId),
          ),
      ),
  );

export const selectLabOrdersForClient = (clientId: string | undefined) =>
  createSelector([selectLabOrdersIds, selectLabOrdersEntities], (ids, entities): ServiceRequest[] =>
    ids
      .map((id) => entities[id])
      .filter(
        (labOrder): labOrder is ServiceRequest =>
          !!labOrder &&
          !!labOrder.subject &&
          !!clientId &&
          getReferenceString(labOrder.subject) === joinReferenceString("Patient", clientId),
      ),
  );

export const selectLabOrdersForEncounter = (encounterId: string | undefined) =>
  createSelector([selectLabOrdersIds, selectLabOrdersEntities], (ids, entities): ServiceRequest[] =>
    ids
      .map((id) => entities[id])
      .filter(
        (labOrder): labOrder is ServiceRequest =>
          !!labOrder &&
          !!labOrder.encounter &&
          !!encounterId &&
          getReferenceString(labOrder.encounter) === joinReferenceString("Encounter", encounterId),
      ),
  );

export default labOrdersSlice.reducer;
